<template>
  <div class="enroll-recruit-sync">
    <header class="page-toolbar">
      <div class="page-identity">
        <el-button
          icon="el-icon-back"
          size="mini"
          @click="goBack">
          返回
        </el-button>
        <span class="toolbar-divider"></span>
        <div class="title-group">
          <h1>报名录取数据同步</h1>
          <span v-if="gradeInfo.gradeName" class="grade-name">{{ gradeInfo.gradeName }}</span>
          <span v-else-if="contextLoading" class="grade-name">年级加载中</span>
        </div>
      </div>
      <div class="query-tools">
        <el-input
          v-model="keyword"
          clearable
          size="mini"
          placeholder="姓名 / 证件号 / 手机号 / 报名号 / 准考证号"
          @keyup.enter.native="handleSearch"
          @clear="handleSearch">
        </el-input>
        <el-button
          icon="el-icon-search"
          size="mini"
          type="primary"
          @click="handleSearch">
          查询
        </el-button>
        <el-tooltip content="刷新全部分类" placement="bottom">
          <el-button
            aria-label="刷新全部分类"
            icon="el-icon-refresh"
            size="mini"
            @click="refreshAll">
          </el-button>
        </el-tooltip>
      </div>
    </header>

    <section class="action-band">
      <div class="selection-state">
        <span>已选择</span>
        <strong>{{ selectedCount }}</strong>
        <span>名未同步学生</span>
      </div>
      <div class="primary-actions">
        <el-button
          :disabled="selectedCount === 0 || syncAll.running"
          :loading="syncingSelected"
          icon="el-icon-circle-check"
          size="mini"
          type="primary"
          @click="syncSelected">
          同步选中学生
        </el-button>
        <el-button
          :disabled="syncingSelected"
          :loading="syncAll.running"
          icon="el-icon-upload2"
          size="mini"
          type="success"
          @click="startSyncAll">
          同步全部未同步学生
        </el-button>
      </div>
    </section>

    <section v-if="syncAll.running" class="task-state">
      <div class="task-copy">
        <i class="el-icon-loading"></i>
        <span>{{ syncAll.statusText || '正在执行同步任务' }}</span>
      </div>
      <el-progress
        :percentage="syncAll.percentage"
        :show-text="false"
        :stroke-width="6">
      </el-progress>
    </section>

    <section class="category-workspace">
      <el-tabs
        v-model="activeCategory"
        @tab-click="handleCategoryTabClick">
        <el-tab-pane
          v-for="category in categories"
          :key="category.key"
          :name="category.key">
          <span slot="label" class="category-label">
            {{ category.label }}
            <span class="loaded-count">
              {{ category.rows.length }}{{ category.hasMore ? '+' : '' }}
            </span>
          </span>

          <div v-if="category.error && !category.loading" class="category-error">
            <i class="el-icon-warning-outline"></i>
            <span>{{ category.error }}</span>
            <el-button
              icon="el-icon-refresh"
              size="mini"
              type="text"
              @click="loadCategory(category, true)">
              重试
            </el-button>
          </div>

          <div v-else class="category-result">
            <div class="table-viewport">
              <el-table
                :ref="`table-${category.key}`"
                v-loading="category.loading"
                :data="category.rows"
                :empty-text="category.loading ? '正在加载' : '暂无数据'"
                border
                height="100%"
                row-key="id"
                size="mini"
                stripe
                @selection-change="handleSelectionChange(category, $event)">
                <el-table-column
                  :selectable="isRowSelectable"
                  reserve-selection
                  type="selection"
                  width="44">
                </el-table-column>
                <el-table-column
                  fixed="left"
                  label="同步状态"
                  prop="syncStatus"
                  width="92">
                  <template slot-scope="scope">
                    <el-tag
                      :type="statusType(scope.row.syncStatus)"
                      effect="plain"
                      size="mini">
                      {{ scope.row.syncStatus }}
                    </el-tag>
                  </template>
                </el-table-column>
                <el-table-column
                  label="不可同步原因"
                  min-width="220"
                  prop="unavailableReason"
                  show-overflow-tooltip>
                  <template slot-scope="scope">
                    {{ scope.row.unavailableReason || '--' }}
                  </template>
                </el-table-column>
                <el-table-column label="招生批次" min-width="160" prop="batchName" show-overflow-tooltip></el-table-column>
                <el-table-column label="姓名" min-width="90" prop="trueName"></el-table-column>
                <el-table-column label="性别" prop="genderName" width="64"></el-table-column>
                <el-table-column label="证件类型" min-width="100" prop="cardTypeName"></el-table-column>
                <el-table-column label="证件号码" min-width="170" prop="cardNo" show-overflow-tooltip></el-table-column>
                <el-table-column label="手机号" min-width="120" prop="mobilePhone"></el-table-column>
                <el-table-column label="报考教学点" min-width="140" prop="siteName" show-overflow-tooltip></el-table-column>
                <el-table-column label="缴费状态" min-width="90" prop="tuitionStatusName"></el-table-column>
                <el-table-column label="报名号" min-width="120" prop="enrollNo" show-overflow-tooltip></el-table-column>
                <el-table-column label="准考证号" min-width="120" prop="examNo" show-overflow-tooltip></el-table-column>
              </el-table>
            </div>

            <footer v-if="!category.loading" class="table-footer">
              <span>已加载 {{ category.rows.length }} 人</span>
              <el-button
                v-if="category.hasMore"
                :loading="category.loadingMore"
                icon="el-icon-arrow-down"
                size="mini"
                @click="loadCategory(category, false)">
                加载更多
              </el-button>
            </footer>
          </div>
        </el-tab-pane>
      </el-tabs>
    </section>
  </div>
</template>

<script>
const PAGE_SIZE = 100
const API_ROOT = '/workspace/manager/enroll/recruit/sync'

function createCategory (key, label, studentType, admissionMethod) {
  return {
    key,
    label,
    studentType,
    admissionMethod,
    rows: [],
    selectedRows: [],
    loading: false,
    loadingMore: false,
    error: '',
    pageIndex: 0,
    hasMore: false,
    requestVersion: 0
  }
}

export default {
  name: 'EnrollRecruitSync',
  data () {
    return {
      gradeId: '',
      gradeInfo: {},
      contextLoading: false,
      keyword: '',
      appliedKeyword: '',
      activeCategory: 'MBA_WRITE',
      categories: [
        createCategory('MBA_WRITE', 'MBA 笔试录取', 'MBA', 'WRITE'),
        createCategory('MBA_EXEMPT', 'MBA 免笔试录取', 'MBA', 'EXEMPT'),
        createCategory('EMBA_WRITE', 'EMBA 笔试录取', 'EMBA', 'WRITE'),
        createCategory('EMBA_EXEMPT', 'EMBA 免笔试录取', 'EMBA', 'EXEMPT'),
        createCategory('FOLLOW', '跟读生', null, 'FOLLOW')
      ],
      syncingSelected: false,
      syncAll: {
        running: false,
        percentage: 0,
        statusText: ''
      },
      syncAllObservedRunning: false,
      syncAllTimer: null,
      hasActivatedOnce: false
    }
  },
  computed: {
    selectedRows () {
      const rowMap = {}
      this.categories.forEach(category => {
        category.selectedRows.forEach(row => {
          rowMap[row.id] = row
        })
      })
      return Object.keys(rowMap).map(id => rowMap[id])
    },
    selectedCount () {
      return this.selectedRows.length
    }
  },
  watch: {
    '$route.query.grade' (gradeId) {
      if (gradeId && gradeId !== this.gradeId) {
        this.initialize()
      }
    }
  },
  created () {
    this.initialize()
  },
  activated () {
    if (this.gradeId) {
      if (this.hasActivatedOnce) {
        this.loadContext()
        this.loadAllCategories()
      }
      this.hasActivatedOnce = true
      this.querySyncAllState(false)
    }
  },
  deactivated () {
    this.stopSyncAllPolling()
  },
  beforeDestroy () {
    this.stopSyncAllPolling()
  },
  methods: {
    initialize () {
      this.stopSyncAllPolling()
      this.gradeId = this.$route.query.grade || ''
      this.gradeInfo = {}
      this.syncAll.running = false
      this.syncAll.percentage = 0
      this.syncAll.statusText = ''
      this.syncAllObservedRunning = false
      this.resetCategories()
      if (!this.gradeId) {
        this.$message.error('未获取到录取新生列表的年级，请返回后重新进入')
        return
      }
      this.loadContext()
      this.loadAllCategories()
      this.querySyncAllState(false)
    },
    resetCategories () {
      this.categories.forEach(category => {
        category.rows = []
        category.selectedRows = []
        category.loading = false
        category.loadingMore = false
        category.error = ''
        category.pageIndex = 0
        category.hasMore = false
        category.requestVersion++
      })
      this.clearTableSelections()
    },
    loadContext () {
      this.contextLoading = true
      this.$ajax({
        url: `${API_ROOT}/context`,
        params: { grade: this.gradeId }
      }).then(response => {
        const data = this.requireSuccess(response)
        this.gradeInfo = {
          gradeId: data.gradeId,
          gradeName: data.gradeName,
          admissionYear: data.admissionYear
        }
      }).catch(error => {
        this.$message.error(error.message || '年级信息加载失败')
      }).finally(() => {
        this.contextLoading = false
      })
    },
    loadAllCategories () {
      this.categories.forEach(category => this.loadCategory(category, true))
    },
    loadCategory (category, reset) {
      if (!this.gradeId || (!reset && (category.loading || category.loadingMore))) {
        return
      }
      if (reset) {
        category.requestVersion++
      }
      const requestVersion = category.requestVersion
      const nextPage = reset ? 1 : category.pageIndex + 1
      if (reset) {
        category.loading = true
        category.error = ''
        category.selectedRows = []
        this.$nextTick(() => {
          const tableRef = this.$refs[`table-${category.key}`]
          const table = Array.isArray(tableRef) ? tableRef[0] : tableRef
          if (table && table.clearSelection) {
            table.clearSelection()
          }
        })
      } else {
        category.loadingMore = true
      }
      this.$ajax({
        url: `${API_ROOT}/list`,
        params: {
          grade: this.gradeId,
          studentType: category.studentType,
          admissionMethod: category.admissionMethod,
          keyword: this.appliedKeyword,
          pageIndex: nextPage,
          pageSize: PAGE_SIZE
        }
      }).then(response => {
        const data = this.requireSuccess(response)
        if (requestVersion !== category.requestVersion) {
          return
        }
        const rows = data.rows || []
        category.rows = reset ? rows : category.rows.concat(rows)
        category.pageIndex = nextPage
        category.hasMore = data.hasMore === true
        category.error = ''
      }).catch(error => {
        if (requestVersion === category.requestVersion) {
          category.error = error.message || '分类数据加载失败'
        }
      }).finally(() => {
        if (requestVersion === category.requestVersion) {
          category.loading = false
          category.loadingMore = false
        }
      })
    },
    requireSuccess (response) {
      const data = response && response.data
      if (!data || data.responseCode !== 'SUCCESS') {
        throw new Error((data && data.message) || '请求失败')
      }
      return data
    },
    handleSearch () {
      this.appliedKeyword = this.keyword.trim()
      this.loadAllCategories()
    },
    refreshAll () {
      this.loadContext()
      this.loadAllCategories()
      this.querySyncAllState(false)
    },
    handleSelectionChange (category, rows) {
      category.selectedRows = rows
    },
    handleCategoryTabClick (tab) {
      const categoryKey = tab && tab.name ? tab.name : this.activeCategory
      this.$nextTick(() => {
        const layout = () => {
          const tableRef = this.$refs[`table-${categoryKey}`]
          const table = Array.isArray(tableRef) ? tableRef[0] : tableRef
          if (table && table.doLayout) {
            table.doLayout()
          }
        }
        if (window.requestAnimationFrame) {
          window.requestAnimationFrame(layout)
        } else {
          window.setTimeout(layout, 0)
        }
      })
    },
    clearTableSelections () {
      this.$nextTick(() => {
        this.categories.forEach(category => {
          const tableRef = this.$refs[`table-${category.key}`]
          const table = Array.isArray(tableRef) ? tableRef[0] : tableRef
          if (table && table.clearSelection) {
            table.clearSelection()
          }
        })
      })
    },
    isRowSelectable (row) {
      return row.syncStatus === '未同步' && !this.syncAll.running && !this.syncingSelected
    },
    statusType (status) {
      return {
        已同步: 'success',
        数据冲突: 'warning',
        数据异常: 'danger'
      }[status] || ''
    },
    syncSelected () {
      if (this.selectedCount === 0) {
        this.$message.warning('请选择要同步的录取学生')
        return
      }
      if (this.selectedCount > 500) {
        this.$message.warning('单次最多同步500名学生')
        return
      }
      const gradeName = this.gradeInfo.gradeName || '当前年级'
      this.$confirm(`确定将选中的${this.selectedCount}名学生同步到【${gradeName}】录取新生列表吗？`, '同步选中学生', {
        type: 'warning',
        confirmButtonText: '确定同步',
        cancelButtonText: '取消'
      }).then(() => {
        this.syncingSelected = true
        return this.$ajax({
          url: `${API_ROOT}/handleBatchSync`,
          method: 'post',
          data: this.$qs.stringify({
            grade: this.gradeId,
            studentIds: this.selectedRows.map(row => row.id)
          }, {
            arrayFormat: 'repeat'
          })
        })
      }).then(response => {
        const data = this.requireSuccess(response)
        this.$message.success(data.message || '同步成功')
        this.loadAllCategories()
      }).catch(error => {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error(error.message || '同步失败')
        }
      }).finally(() => {
        this.syncingSelected = false
      })
    },
    startSyncAll () {
      const gradeName = this.gradeInfo.gradeName || '当前年级'
      this.$confirm(`确定同步【${gradeName}】全部未同步学生吗？任务将在后台执行。`, '同步全部未同步学生', {
        type: 'warning',
        confirmButtonText: '启动同步',
        cancelButtonText: '取消'
      }).then(() => {
        return this.$ajax({
          url: `${API_ROOT}/handleSyncAll`,
          method: 'post',
          data: this.$qs.stringify({ grade: this.gradeId })
        })
      }).then(response => {
        const data = this.requireSuccess(response)
        this.syncAll.running = true
        this.syncAll.percentage = 0
        this.syncAll.statusText = '同步任务已提交'
        this.syncAllObservedRunning = true
        this.$message.success(data.message || '同步任务已提交')
        this.startSyncAllPolling()
      }).catch(error => {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error(error.message || '同步任务启动失败')
        }
      })
    },
    querySyncAllState (notifyCompletion) {
      if (!this.gradeId) {
        return
      }
      this.$ajax({
        url: `${API_ROOT}/handleSyncAllState`,
        params: { grade: this.gradeId }
      }).then(response => {
        const data = this.requireSuccess(response)
        if (!data.exists) {
          this.syncAll.running = false
          this.stopSyncAllPolling()
          return
        }
        if (!data.isDone) {
          this.syncAll.running = true
          this.syncAll.percentage = data.percentage || 0
          this.syncAll.statusText = data.statusText || '正在执行同步任务'
          this.syncAllObservedRunning = true
          this.startSyncAllPolling()
          return
        }
        const shouldNotify = notifyCompletion && this.syncAllObservedRunning
        this.syncAll.running = false
        this.syncAll.percentage = 100
        this.syncAll.statusText = '同步完成'
        this.stopSyncAllPolling()
        if (shouldNotify) {
          this.$message.success(`成功同步${data.successCount || 0}名录取新生`)
          this.loadAllCategories()
        }
        this.syncAllObservedRunning = false
      }).catch(error => {
        this.syncAll.running = false
        this.stopSyncAllPolling()
        if (notifyCompletion || this.syncAllObservedRunning) {
          this.$message.error(error.message || '同步任务执行失败')
        }
        this.syncAllObservedRunning = false
      })
    },
    startSyncAllPolling () {
      if (this.syncAllTimer) {
        return
      }
      this.syncAllTimer = window.setInterval(() => {
        this.querySyncAllState(true)
      }, 1500)
    },
    stopSyncAllPolling () {
      if (this.syncAllTimer) {
        window.clearInterval(this.syncAllTimer)
        this.syncAllTimer = null
      }
    },
    goBack () {
      this.$router.go(-1)
    }
  }
}
</script>

<style lang="scss" scoped>
.enroll-recruit-sync {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: #f5f7fa;
  color: #303133;
}

.page-toolbar,
.action-band,
.task-state,
.category-workspace {
  background: #fff;
}

.page-toolbar {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 58px;
  padding: 10px 16px;
  border-bottom: 1px solid #dcdfe6;
}

.page-identity,
.query-tools,
.action-band,
.primary-actions,
.selection-state,
.task-copy {
  display: flex;
  align-items: center;
}

.page-identity {
  min-width: 0;
}

.toolbar-divider {
  width: 1px;
  height: 28px;
  margin: 0 14px;
  background: #dcdfe6;
}

.title-group {
  display: flex;
  align-items: baseline;
  min-width: 0;

  h1 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    letter-spacing: 0;
    white-space: nowrap;
  }
}

.grade-name {
  margin-left: 12px;
  color: #606266;
  font-size: 13px;
  white-space: nowrap;
}

.query-tools {
  gap: 8px;

  .el-input {
    width: 320px;
  }
}

.action-band {
  flex: 0 0 auto;
  justify-content: space-between;
  gap: 16px;
  min-height: 52px;
  padding: 8px 16px;
  border-bottom: 1px solid #ebeef5;
}

.selection-state {
  color: #606266;
  font-size: 13px;

  strong {
    min-width: 24px;
    margin: 0 5px;
    color: #409eff;
    font-size: 18px;
    text-align: center;
  }
}

.primary-actions {
  gap: 8px;

  .el-button + .el-button {
    margin-left: 0;
  }
}

.task-state {
  display: grid;
  flex: 0 0 auto;
  grid-template-columns: minmax(180px, 280px) minmax(180px, 1fr);
  align-items: center;
  gap: 20px;
  padding: 10px 16px;
  border-bottom: 1px solid #ebeef5;
}

.task-copy {
  gap: 8px;
  color: #409eff;
  font-size: 13px;
}

.category-workspace {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  margin-top: 10px;
  padding: 0 16px 16px;
  overflow: hidden;

  ::v-deep .el-tabs {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
  }

  ::v-deep .el-tabs__header {
    flex: 0 0 auto;
  }

  ::v-deep .el-tabs__content {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
  }

  ::v-deep .el-tab-pane {
    height: 100%;
  }
}

.category-result {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.table-viewport {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.category-label {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

.loaded-count {
  min-width: 20px;
  padding: 0 5px;
  border-radius: 3px;
  background: #f0f2f5;
  color: #606266;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
}

.category-error {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 100%;
  min-height: 0;
  color: #f56c6c;
  font-size: 13px;
}

.table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 42px;
  padding: 6px 4px 0;
  color: #909399;
  font-size: 12px;
}

@media (max-width: 900px) {
  .page-toolbar,
  .action-band {
    align-items: stretch;
    flex-direction: column;
  }

  .query-tools {
    width: 100%;

    .el-input {
      flex: 1;
      width: auto;
    }
  }

  .action-band {
    min-height: auto;
  }

  .primary-actions {
    flex-wrap: wrap;
  }

  .task-state {
    grid-template-columns: 1fr;
    gap: 8px;
  }
}
</style>
